import { getMessageWithPlaceholders } from 'shared-locales';
import { useReduxState } from 'hooks';
import { getCustomAttributesTypes } from 'lib/customAttributes';
import {
  Box,
  BoxOverflowWrapper,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import {
  Badge,
  EmptyIndicator,
  FlexContainer,
  Link,
  MoreOptions
} from 'components/common';
import { AttributeTranslationsInfo } from 'components/common/customAttributes';
import { Icon } from 'components/common/icons';
import {
  UserAttributesAdd,
  UserAttributesConvertToArray,
  UserAttributesDelete,
  UserAttributesEdit,
  UserAttributesEnable,
  UserAttributesExport,
  UserAttributesImport,
  UserAttributesOrder,
  UserAttributesPermissionsBadge
} from '.';

import type { AttributeCondition } from 'shared-types';
import type { GetUserCustomAttributesRes } from 'shared-types/responses';

interface Props {
  updateData: () => Promise<void>;
  userCustomAttributes: GetUserCustomAttributesRes['userCustomAttributes'];
}

const UserAttributesList = ({ updateData, userCustomAttributes }: Props) => {
  const { messages, theme } = useReduxState();

  return (
    <div className="user-attributes-list">
      <Box
        headerContent={
          <FlexContainer style={{ gap: 10 }}>
            <UserAttributesAdd
              updateData={updateData}
              userCustomAttributes={userCustomAttributes}
            />
            <MoreOptions category="lighter" styleContentButtons={false}>
              <UserAttributesEnable
                attributes={userCustomAttributes}
                updateData={updateData}
              />
              <UserAttributesOrder
                attributes={userCustomAttributes}
                updateData={updateData}
              />
              <UserAttributesExport attributes={userCustomAttributes} />
              <UserAttributesImport
                existingAttributes={userCustomAttributes}
                updateData={updateData}
              />
            </MoreOptions>
          </FlexContainer>
        }
        padding={0}
        title={messages.userAttributes}
      >
        {userCustomAttributes?.length ? (
          <BoxOverflowWrapper>
            <BoxTable>
              <BoxTableHeader>
                <BoxTableCell></BoxTableCell>
                <BoxTableCell>{messages.commonAttribute}</BoxTableCell>
                <BoxTableCell>
                  {messages.userAttributesFormApplies}
                </BoxTableCell>
                <BoxTableCell>{messages.commonType}</BoxTableCell>
                <BoxTableCell>{messages.commonRequired}</BoxTableCell>
                <BoxTableCell>
                  {messages.userAttributesFormVisible}
                </BoxTableCell>
                <BoxTableCell>{messages.commonEditingRights}</BoxTableCell>
                <BoxTableCell></BoxTableCell>
              </BoxTableHeader>

              {userCustomAttributes.map((attribute) => (
                <BoxTableRow key={attribute.id}>
                  <BoxTableCell shrink={true} style={{ paddingRight: 0 }}>
                    <div
                      className="enabled"
                      aria-label={
                        attribute.isEnabled
                          ? messages.commonEnabled
                          : messages.commonDisabled
                      }
                      data-tooltip={
                        attribute.isEnabled
                          ? messages.commonEnabled
                          : messages.commonDisabled
                      }
                    >
                      <Icon
                        icon="check-circle"
                        style={{
                          color: attribute.isEnabled
                            ? theme.successStandaloneColor
                            : 'rgba(0, 0, 0, 0.15)'
                        }}
                      />
                    </div>
                  </BoxTableCell>
                  <BoxTableCell>
                    <Link
                      href={`/admin/settings/user-attributes/${attribute.id}`}
                      linkColor="inherit"
                      text={attribute.title}
                      style={{ marginRight: 10 }}
                    />

                    <AttributeTranslationsInfo
                      translations={attribute.titleTranslations}
                    />

                    <div className="subtitle">
                      {`${messages.attributesKey}: ${attribute.key}`}
                    </div>
                  </BoxTableCell>
                  <BoxTableCell>
                    <div className="badge-container">
                      {[
                        {
                          text: messages.commonUser,
                          value: attribute.appliesTo?.user
                        },
                        {
                          text: messages.usersAdmin,
                          value: attribute.appliesTo?.admin
                        },
                        {
                          text: messages.usersGuest,
                          value: attribute.appliesTo?.guest
                        }
                      ]
                        .filter((item) => item.value)
                        .map((item) => (
                          <Badge
                            key={item.text}
                            size="small"
                            text={item.text}
                          />
                        ))}
                    </div>
                    {attribute.conditions?.length ? (
                      <div className="subtitle">
                        {`${attribute.conditions.length} ${messages.userAttributesConditions}`}
                      </div>
                    ) : null}
                  </BoxTableCell>
                  <BoxTableCell>
                    {getCustomAttributesTypes({ messages })[attribute.type]}
                    {attribute.type === 'Number' ? (
                      <div className="subtitle">
                        {`${attribute.min}-${attribute.max}`}
                      </div>
                    ) : attribute.type === 'ArrayWithOptions' ||
                      attribute.type === 'StringWithOptions' ? (
                      <div className="subtitle">
                        {attribute.optionGroups?.length > 1
                          ? getMessageWithPlaceholders({
                              message: messages.commonNumberOfOptionGroups,
                              placeholders: {
                                numberOfOptionGroups:
                                  attribute.optionGroups.length
                              }
                            })
                          : getMessageWithPlaceholders({
                              message: messages.commonNumberOfOptions,
                              placeholders: {
                                numberOfOptions:
                                  attribute.optionGroups?.[0]?.options.length
                              }
                            })}
                      </div>
                    ) : null}
                  </BoxTableCell>
                  <BoxTableCell
                    tooltip={
                      attribute.isRequired?.conditions?.length
                        ? `${messages.userAttributesConditions}: ` +
                          attribute.isRequired.conditions
                            .map(
                              (condition: AttributeCondition) =>
                                `${condition.key} ${condition.expression} ${condition.value}`
                            )
                            .join(', ')
                        : null
                    }
                  >
                    {attribute.isRequired?.value &&
                    attribute.isRequired?.conditions?.length
                      ? messages.commonYes + '*'
                      : attribute.isRequired?.value
                      ? messages.commonYes
                      : null}
                  </BoxTableCell>
                  <BoxTableCell shrink={true}>
                    <div className="badge-container">
                      {[
                        {
                          conditions:
                            attribute.permissions.view?.user.conditions,
                          text: messages.commonUser,
                          value: attribute.permissions.view?.user.value
                        },
                        {
                          conditions:
                            attribute.permissions.view?.admin.conditions,
                          text: messages.usersAdmin,
                          value: attribute.permissions.view?.admin.value
                        },
                        {
                          conditions:
                            attribute.permissions.view?.superadmin.conditions,
                          text: messages.usersSuperadmin,
                          value: attribute.permissions.view?.superadmin.value
                        }
                      ]
                        .filter((item) => item.value)
                        .map((item) => (
                          <UserAttributesPermissionsBadge
                            conditions={item.conditions}
                            key={item.text}
                            text={item.text}
                            userCustomAttributes={userCustomAttributes}
                          />
                        ))}
                    </div>
                  </BoxTableCell>
                  <BoxTableCell shrink={true}>
                    <div className="badge-container">
                      {[
                        {
                          conditions:
                            attribute.permissions.edit?.user.conditions,
                          text: messages.commonUser,
                          value: attribute.permissions.edit?.user.value
                        },
                        {
                          conditions:
                            attribute.permissions.edit?.admin.conditions,
                          text: messages.usersAdmin,
                          value: attribute.permissions.edit?.admin.value
                        },
                        {
                          conditions:
                            attribute.permissions.edit?.superadmin.conditions,
                          text: messages.usersSuperadmin,
                          value: attribute.permissions.edit?.superadmin.value
                        }
                      ]
                        .filter((item) => item.value)
                        .map((item) => (
                          <UserAttributesPermissionsBadge
                            conditions={item.conditions}
                            key={item.text}
                            text={item.text}
                            userCustomAttributes={userCustomAttributes}
                          />
                        ))}
                    </div>
                  </BoxTableCell>
                  <BoxTableCell shrink={true}>
                    <MoreOptions
                      renderAsFixed={true}
                      styleContentButtons={false}
                    >
                      <UserAttributesEdit
                        attribute={attribute}
                        isDropdown={true}
                        updateData={updateData}
                        userCustomAttributes={userCustomAttributes}
                      />
                      {['String', 'StringWithOptions'].includes(
                        attribute.type
                      ) ? (
                        <UserAttributesConvertToArray
                          attribute={attribute}
                          updateData={updateData}
                        />
                      ) : null}
                      <UserAttributesDelete
                        attributeId={attribute.id}
                        updateData={updateData}
                      />
                    </MoreOptions>
                  </BoxTableCell>
                </BoxTableRow>
              ))}
            </BoxTable>
          </BoxOverflowWrapper>
        ) : (
          <EmptyIndicator icon="tag" title={messages.attributesEmpty} />
        )}
      </Box>

      <style jsx>{`
        .subtitle {
          color: ${theme.textColorLighter};
          font-size: 1.2rem;
          font-weight: 400;
          margin-top: 2px;
          white-space: nowrap;
        }
        .badge-container {
          display: flex;
          flex-wrap: wrap;
          gap: 5px;
        }
      `}</style>
    </div>
  );
};

export default UserAttributesList;
